import { createContext, useContext, useEffect, useCallback, useState } from "react";
import { isImage, isVideo, StorageUtil } from "utils";
import { UPLOAD_FILE_TYPE, useUploadFileContext } from "./UploadFileContext";
import { ConversationSelectors, SystemSelectors } from "redux-store";
import { createSelector } from "reselect";
import { KeyConstant } from "const";
import { useSelector } from "react-redux";

/* ------------- Initial State ------------- */
const INITIAL_STATE = { isDragover: false };

/* ------------- Context ------------- */
const DropFileContext = createContext(INITIAL_STATE);
export const useDropFileContext = () => {
  const context = useContext(DropFileContext);
  if (!context) throw new Error("useDropFileContext must be used within DropFileProvider");
  return context;
};

/* ------------- Provider ------------- */
const memoizedReduxState = createSelector(
  [SystemSelectors.checkScreenLock, SystemSelectors.isShowMnFile, ConversationSelectors.getSelectedGroupId],
  (screenLock, isShowMnFile, selectedGroupId) => {
    return {
      isDisableDrop: Boolean(
        isShowMnFile ||
          screenLock?.isShow ||
          StorageUtil.getCommonKey(KeyConstant.SCREEN_LOCK_STATUS) ||
          !selectedGroupId,
      ),
    };
  },
);
export const DropFileProvider = ({ children }) => {
  const { isDisableDrop } = useSelector(memoizedReduxState);
  const { isShowUploadDialog, uploadFiles, setFileType } = useUploadFileContext();

  const [isDragover, setIsDragover] = useState(false);

  const handleDrop = useCallback(
    event => {
      event.preventDefault();
      if (isShowUploadDialog) {
        // Skip if dialog is showing
        return false;
      }

      // Maximum 9 files
      const dropFiles = Array.from(event.dataTransfer?.files || []).slice(0, 9);
      const isOnlyMediaFiles = Array.from(dropFiles).every(item => isImage(item.type) || isVideo(item.type));

      setFileType(isOnlyMediaFiles ? UPLOAD_FILE_TYPE.media : UPLOAD_FILE_TYPE.file);
      uploadFiles(dropFiles);
    },
    [isShowUploadDialog],
  );

  useEffect(() => {
    if (!isDisableDrop && !isShowUploadDialog) {
      const handleDrag = isDrag => event => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        setIsDragover(isDrag);

        // Allow files only inside this zone
        if (event.dataTransfer.types.includes("Files")) {
          event.dataTransfer.dropEffect = "copy"; // show "+" cursor
        }
      };
      window.addEventListener("dragover", handleDrag(true));
      window.addEventListener("dragleave", handleDrag(false));
      window.addEventListener("drop", handleDrop);

      return () => {
        window.removeEventListener("dragover", handleDrag(true));
        window.removeEventListener("dragleave", handleDrag(false));
        window.removeEventListener("drop", handleDrop);
      };
    } else {
      window.addEventListener("dragover", event => {
        event.preventDefault();
        event.stopPropagation();

        // Deny files explicitly
        if (event.dataTransfer && event.dataTransfer.types.includes("Files")) {
          event.dataTransfer.dropEffect = "none"; // 🚫 deny cursor
        }
      });
    }
  }, [isShowUploadDialog, isDisableDrop]);

  return <DropFileContext.Provider value={{ isDragover }}>{children}</DropFileContext.Provider>;
};
